import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DataTable,
  Select,
  TextInput,
  Badge
} from '@etherisc/ui-kit';
import { FileText, BarChart3, Users, Download } from 'lucide-react';
import { 
  useClientReceivablesAging,
  useMultiClientBalanceSummary 
} from '../../hooks/insurance/useInsuranceReporting';
import { 
  useReconciliationReport,
  useCommissionSummary 
} from '../../hooks/insurance/useInsuranceBatch';
import { useLedgerReports } from '../../hooks/ledger/useLedgerReports';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { 
  exportPremiumAging, 
  exportClientBalances, 
  exportCommissionSummary, 
  exportReconciliationReport,
  exportFinancialAnalytics 
} from '../../utils/exportUtils';

export function InsuranceReports() {
  const [activeReport, setActiveReport] = useState<'aging' | 'client-summary' | 'reconciliation' | 'commission'>('aging');
  const [asOfDate, setAsOfDate] = useState<Date>(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');

  const { periods } = useLedgerReports();
  const { data: agingData, isLoading: agingLoading, refetch: refetchAging } = useClientReceivablesAging(asOfDate);
  const { data: balanceData, isLoading: balanceLoading } = useMultiClientBalanceSummary({ periodId: selectedPeriod });
  const { data: reconciliationData, isLoading: reconciliationLoading } = useReconciliationReport({ periodId: selectedPeriod });
  const { data: commissionData, isLoading: commissionLoading, error: commissionError } = useCommissionSummary({});

  // Premium Aging Report Component
  const PremiumAgingReport = () => {
    const agingColumns = [
      {
        accessorKey: 'clientId',
        header: 'Client ID',
        cell: ({ row }: { row: { original: unknown } }) => {
          const aging = row.original as { clientId: string };
          return (
            <span className="font-mono text-sm">{aging.clientId.substring(0, 8)}...</span>
          );
        },
      },
      {
        accessorKey: 'costCenterId',
        header: 'Policy',
        cell: ({ row }: { row: { original: unknown } }) => {
          const aging = row.original as { costCenterId?: string };
          return (
            <span className="font-mono text-sm">
              {aging.costCenterId ? aging.costCenterId.substring(0, 8) + '...' : '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'currency',
        header: 'Currency',
        cell: ({ row }: { row: { original: unknown } }) => {
          const aging = row.original as { currency: string };
          return (
            <span className="font-mono text-sm">{aging.currency}</span>
          );
        },
      },
      {
        accessorKey: 'agingBucket',
        header: 'Aging Bucket',
        cell: ({ row }: { row: { original: unknown } }) => {
          const aging = row.original as { agingBucket: string };
          const bucket = aging.agingBucket;
          const variant = bucket === 'current' ? 'success' :
                          bucket === '30_days' ? 'default' :
                          bucket === '60_days' ? 'warning' : 'destructive';
          
          const label = bucket === 'current' ? 'Current' :
                       bucket === '30_days' ? '30 Days' :
                       bucket === '60_days' ? '60 Days' : '90+ Days';
          
          return <Badge variant={variant}>{label}</Badge>;
        },
      },
      {
        accessorKey: 'outstandingAmount',
        header: 'Outstanding Amount',
        cell: ({ row }: { row: { original: unknown } }) => {
          const aging = row.original as { outstandingAmount: string; currency: string };
          return (
            <span className="font-semibold">
              {formatCurrency(aging.outstandingAmount, aging.currency)}
            </span>
          );
        },
      }
    ];

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Premium Aging Report</CardTitle>
            <div className="flex gap-2">
              <TextInput
                type="date"
                value={asOfDate.toISOString().split('T')[0]}
                onChange={(e) => setAsOfDate(new Date(e.target.value))}
                className="w-auto"
              />
              <Button onClick={() => refetchAging()}>
                Generate Report
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => agingData && exportPremiumAging(agingData, asOfDate)}
                disabled={!agingData || agingData.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {agingLoading ? (
            <div className="text-center py-8">Loading aging report...</div>
          ) : agingData && agingData.length > 0 ? (
            <DataTable
              data={agingData}
              columns={agingColumns}
              loading={agingLoading}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No outstanding premiums found as of {formatDate(asOfDate.toISOString())}.
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Client Summary Report Component
  const ClientSummaryReport = () => {
    const balanceColumns = [
      {
        accessorKey: 'clientName',
        header: 'Client',
        cell: ({ row }: { row: { original: unknown } }) => {
          const client = row.original as { clientName: string; clientCode: string };
          return (
            <div>
              <div className="font-medium">{client.clientName}</div>
              <div className="text-sm text-muted-foreground">{client.clientCode}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'totalPremiumDue',
        header: 'Premium Due',
        cell: ({ row }: { row: { original: unknown } }) => {
          const client = row.original as { totalPremiumDue: string; currency: string };
          return (
            <span className="font-semibold text-blue-600">
              {formatCurrency(client.totalPremiumDue, client.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'totalPaymentsReceived',
        header: 'Payments Received',
        cell: ({ row }: { row: { original: unknown } }) => {
          const client = row.original as { totalPaymentsReceived: string; currency: string };
          return (
            <span className="font-semibold text-green-600">
              {formatCurrency(client.totalPaymentsReceived, client.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'outstandingBalance',
        header: 'Outstanding Balance',
        cell: ({ row }: { row: { original: unknown } }) => {
          const client = row.original as { outstandingBalance: string; currency: string };
          return (
            <span className="font-semibold text-orange-600">
              {formatCurrency(client.outstandingBalance, client.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'lastPaymentDate',
        header: 'Last Payment',
        cell: ({ row }: { row: { original: unknown } }) => {
          const client = row.original as { lastPaymentDate?: string };
          return client.lastPaymentDate ? formatDate(client.lastPaymentDate) : '—';
        },
      }
    ];

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Client Balance Summary</CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => balanceData && exportClientBalances(balanceData, selectedPeriod)}
              disabled={!balanceData || balanceData.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Select
              label="Accounting Period"
              value={selectedPeriod || 'none'}
              onValueChange={(value) => setSelectedPeriod(value === 'none' ? '' : value)}
              options={[
                { value: 'none', label: 'Select Period' },
                ...(periods?.map((period: unknown) => {
                  const p = period as { id: string; name: string; startDate: string; endDate: string };
                  return {
                    value: p.id,
                    label: `${p.name} (${formatDate(p.startDate)} - ${formatDate(p.endDate)})`
                  };
                }) || [])
              ]}
            />
            
            {selectedPeriod ? (
              balanceLoading ? (
                <div className="text-center py-8">Loading balance summary...</div>
              ) : balanceData && balanceData.length > 0 ? (
                <DataTable
                  data={balanceData}
                  columns={balanceColumns}
                  loading={balanceLoading}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No client balances found for the selected period.
                </div>
              )
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Please select an accounting period to view client summaries.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Reconciliation Report Component
  const ReconciliationReport = () => {
    const reconciliationColumns = [
      {
        accessorKey: 'accountName',
        header: 'Account',
        cell: ({ row }: { row: { original: unknown } }) => {
          const account = row.original as { accountName: string; accountCode: string };
          return (
            <div>
              <div className="font-medium">{account.accountName}</div>
              <div className="text-sm text-muted-foreground">{account.accountCode}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'glBalance',
        header: 'GL Balance',
        cell: ({ row }: { row: { original: unknown } }) => {
          const account = row.original as { glBalance: string; currency: string };
          return (
            <span className="font-semibold">
              {formatCurrency(account.glBalance, account.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'subledgerBalance',
        header: 'Subledger Balance',
        cell: ({ row }: { row: { original: unknown } }) => {
          const account = row.original as { subledgerBalance: string; currency: string };
          return (
            <span className="font-semibold">
              {formatCurrency(account.subledgerBalance, account.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'variance',
        header: 'Variance',
        cell: ({ row }: { row: { original: unknown } }) => {
          const account = row.original as { variance: string; currency: string };
          const variance = parseFloat(account.variance);
          const isPositive = variance > 0;
          const isZero = variance === 0;
          
          return (
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${
                isZero ? 'text-green-600' : isPositive ? 'text-red-600' : 'text-orange-600'
              }`}>
                {formatCurrency(account.variance, account.currency)}
              </span>
              {isZero && <Badge variant="success">Reconciled</Badge>}
              {!isZero && <Badge variant="destructive">Variance</Badge>}
            </div>
          );
        },
      },
      {
        accessorKey: 'lastReconciled',
        header: 'Last Reconciled',
        cell: ({ row }: { row: { original: unknown } }) => {
          const account = row.original as { lastReconciled?: string };
          return account.lastReconciled ? formatDate(account.lastReconciled) : '—';
        },
      }
    ];

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Financial Reconciliation</CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => reconciliationData?.accounts && exportReconciliationReport(reconciliationData.accounts, selectedPeriod)}
              disabled={!reconciliationData?.accounts || reconciliationData.accounts.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Select
              label="Accounting Period"
              value={selectedPeriod || 'none'}
              onValueChange={(value) => setSelectedPeriod(value === 'none' ? '' : value)}
              options={[
                { value: 'none', label: 'Select Period' },
                ...(periods?.map((period: unknown) => {
                  const p = period as { id: string; name: string; startDate: string; endDate: string };
                  return {
                    value: p.id,
                    label: `${p.name} (${formatDate(p.startDate)} - ${formatDate(p.endDate)})`
                  };
                }) || [])
              ]}
            />
            
            {selectedPeriod ? (
              reconciliationLoading ? (
                <div className="text-center py-8">Loading reconciliation report...</div>
              ) : reconciliationData && reconciliationData.accounts?.length > 0 ? (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Total Accounts</div>
                        <div className="text-2xl font-bold">{reconciliationData.accounts.length}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">Reconciled</div>
                        <div className="text-2xl font-bold text-green-600">
                          {reconciliationData.accounts.filter((acc: unknown) => parseFloat((acc as { variance: string }).variance) === 0).length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground">With Variances</div>
                        <div className="text-2xl font-bold text-red-600">
                          {reconciliationData.accounts.filter((acc: unknown) => parseFloat((acc as { variance: string }).variance) !== 0).length}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <DataTable
                    data={reconciliationData.accounts}
                    columns={reconciliationColumns}
                    loading={reconciliationLoading}
                  />
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No reconciliation data found for the selected period.
                </div>
              )
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Please select an accounting period to view reconciliation report.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Commission Report Component
  const CommissionReport = () => {
    const commissionColumns = [
      {
        accessorKey: 'agentName',
        header: 'Agent',
        cell: ({ row }: { row: { original: unknown } }) => {
          const agent = row.original as { agentName: string; agentCode: string };
          return (
            <div>
              <div className="font-medium">{agent.agentName}</div>
              <div className="text-sm text-muted-foreground">{agent.agentCode}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'commissionDue',
        header: 'Commission Due',
        cell: ({ row }: { row: { original: unknown } }) => {
          const commission = row.original as { commissionDue: string; currency: string };
          return (
            <span className="font-semibold text-blue-600">
              {formatCurrency(commission.commissionDue, commission.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'commissionPaid',
        header: 'Commission Paid',
        cell: ({ row }: { row: { original: unknown } }) => {
          const commission = row.original as { commissionPaid: string; currency: string };
          return (
            <span className="font-semibold text-green-600">
              {formatCurrency(commission.commissionPaid, commission.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'commissionOutstanding',
        header: 'Outstanding',
        cell: ({ row }: { row: { original: unknown } }) => {
          const commission = row.original as { commissionOutstanding: string; currency: string };
          return (
            <span className="font-semibold text-orange-600">
              {formatCurrency(commission.commissionOutstanding, commission.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'policyCount',
        header: 'Policies',
        cell: ({ row }: { row: { original: unknown } }) => {
          const commission = row.original as { policyCount: number };
          return (
            <span className="text-center">{commission.policyCount}</span>
          );
        },
      }
    ];

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Commission Summary</CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => commissionData?.commissions && exportCommissionSummary(commissionData.commissions)}
                disabled={!commissionData?.commissions || commissionData.commissions.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => exportFinancialAnalytics(agingData || [], balanceData || [], commissionData || null, reconciliationData || null, asOfDate)}
                disabled={!agingData && !balanceData && !commissionData}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Export Analytics
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {commissionLoading ? (
            <div className="text-center py-8">Loading commission report...</div>
          ) : commissionError ? (
            <div className="text-center py-8 text-red-600">
              Error loading commission data: {commissionError.message}
            </div>
          ) : commissionData && commissionData.summary && commissionData.commissions && commissionData.commissions.length > 0 ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Total Due</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(commissionData.summary.totalDue, 'EUR')}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Total Paid</div>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(commissionData.summary.totalPaid, 'EUR')}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Outstanding</div>
                    <div className="text-2xl font-bold text-orange-600">
                      {formatCurrency(commissionData.summary.totalOutstanding, 'EUR')}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Active Agents</div>
                    <div className="text-2xl font-bold">{commissionData.summary.agentCount}</div>
                  </CardContent>
                </Card>
              </div>
              
              <DataTable
                data={commissionData.commissions}
                columns={commissionColumns}
                loading={commissionLoading}
              />
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No commission data found. Commissions will appear here once policies are sold.
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Insurance Reports</h1>
        <p className="text-muted-foreground">
          Comprehensive reporting for insurance premium management
        </p>
      </div>

      <Tabs value={activeReport} onValueChange={(value) => setActiveReport(value as 'aging' | 'client-summary' | 'reconciliation' | 'commission')}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="aging" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Premium Aging
          </TabsTrigger>
          <TabsTrigger value="client-summary" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Client Summary
          </TabsTrigger>
          <TabsTrigger value="reconciliation" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Reconciliation
          </TabsTrigger>
          <TabsTrigger value="commission" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Commission
          </TabsTrigger>
        </TabsList>

        <TabsContent value="aging" className="mt-6">
          <PremiumAgingReport />
        </TabsContent>

        <TabsContent value="client-summary" className="mt-6">
          <ClientSummaryReport />
        </TabsContent>

        <TabsContent value="reconciliation" className="mt-6">
          <ReconciliationReport />
        </TabsContent>

        <TabsContent value="commission" className="mt-6">
          <CommissionReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}
